import { useEffect, useState } from "react";
import {
	Autocomplete,
	Box,
	Chip,
	FormControlLabel,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { getServicosDoCliente } from "../api";

// Seleção dos serviços ATIVOS de um cliente para restringir o alcance da regra.
// O padrão é "todos os serviços" — lista vazia no payload (o backend trata
// null/[] como todos). Só faz sentido no escopo CLIENTE.
export default function SeletorServicos({
	clienteErpId,
	clienteId,
	servicoIds = [],
	onChange,
	desabilitado = false,
}) {
	const [servicos, setServicos] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	// Intenção do operador, explícita: derivar "todos" de servicoIds.length === 0
	// fazia o switch voltar sozinho para "todos" enquanto a lista carregava (ou
	// se ela falhasse), e a regra podia ser salva mais abrangente do que ele quis.
	const [querTodos, setQuerTodos] = useState(
		!servicoIds || servicoIds.length === 0
	);

	// Regra existente com serviços salvos abre com o switch desligado.
	useEffect(() => {
		if (servicoIds?.length) setQuerTodos(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const todos = querTodos;

	useEffect(() => {
		let ativo = true;
		if (!clienteErpId) {
			setServicos([]);
			return;
		}
		(async () => {
			try {
				setCarregando(true);
				setErro("");
				const dados = await getServicosDoCliente(clienteErpId, clienteId);
				if (ativo) setServicos(Array.isArray(dados) ? dados : []);
			} catch (_) {
				if (ativo) {
					setServicos([]);
					setErro("Não foi possível carregar os serviços deste cliente.");
				}
			} finally {
				if (ativo) setCarregando(false);
			}
		})();
		return () => {
			ativo = false;
		};
	}, [clienteErpId, clienteId]);

	const rotulo = (s) =>
		s ? `${s.id_cliente_servico} - ${s.nome || s.referencia || ""}`.trim() : "";

	const selecionados = servicos.filter((s) =>
		(servicoIds || []).some((id) => String(id) === String(s.id_cliente_servico))
	);

	return (
		<Box>
			<FormControlLabel
				control={
					<Switch
						checked={todos}
						disabled={desabilitado || !clienteErpId || carregando}
						onChange={(e) => {
							setQuerTodos(e.target.checked);
							onChange(e.target.checked ? [] : selecionarPrimeiro(servicos));
						}}
					/>
				}
				label="Todos os serviços do cliente"
			/>

			{!todos && (
				<Autocomplete
					multiple
					sx={{ mt: 1 }}
					options={servicos}
					value={selecionados}
					loading={carregando}
					disabled={desabilitado || !clienteErpId}
					getOptionLabel={rotulo}
					isOptionEqualToValue={(opt, val) =>
						String(opt?.id_cliente_servico) === String(val?.id_cliente_servico)
					}
					onChange={(_, arr) =>
						onChange(arr.map((s) => String(s.id_cliente_servico)))
					}
					noOptionsText={
						carregando ? "Carregando..." : "Cliente sem serviços ativos"
					}
					renderTags={(valores, getTagProps) =>
						valores.map((s, i) => (
							<Chip
								size="small"
								label={rotulo(s)}
								{...getTagProps({ index: i })}
								key={s.id_cliente_servico}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							size="small"
							label="Serviços"
							error={!!erro}
							helperText={
								erro ||
								"A regra só dispara para alertas dos serviços selecionados"
							}
						/>
					)}
				/>
			)}

			{todos && (
				<Typography variant="caption" color="text.secondary">
					{carregando
						? "Carregando serviços..."
						: erro ||
						  `A regra vale para todos os serviços ativos${
								servicos.length ? ` (${servicos.length})` : ""
						  } do cliente.`}
				</Typography>
			)}
		</Box>
	);
}

// Ao desligar "todos", já deixa o primeiro serviço marcado — evita o estado
// intermediário "nenhum serviço", que o backend leria como "todos".
function selecionarPrimeiro(servicos) {
	const primeiro = servicos?.[0];
	return primeiro ? [String(primeiro.id_cliente_servico)] : [];
}
